import { useOktaAuth } from '@okta/okta-react';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../common/hooks';
import { updateOktaAuthState } from '../features/auth/authSlice';
import { updateGlobalLoaderState } from '../features/global/globalSlice';
import { onCrossTabLogout, triggerSessionExpiredLogout } from '../common/sessionLogout';
import { ADVISOR_ROUTES } from '../routes/routeConstants';

interface AuthProviderProps {
    children: React.ReactNode;
}

const AUTH_ENTRY_ROUTES = [ADVISOR_ROUTES.ROOT, ADVISOR_ROUTES.LOGIN, '/login/callback'];

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const { authState, oktaAuth } = useOktaAuth();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { error, isAuthenticated: isAuthenticatedFromRedux } = useAppSelector((state) => state.auth);
    const isHantzTenant = useAppSelector((state) => state?.auth?.user?.isHantzTenant);
    const tenantsList = useAppSelector((state) => state?.auth?.user?.tenants);

    // Initialize Redux state with Okta auth state
    useEffect(() => {
        if (!authState) return;
        const checkAuth = async () => {
            const isAuthenticated = await oktaAuth.isAuthenticated();
            const currentPath = location.pathname;
            console.log('AuthProvider navigation check 11:', {
                authState,
                isAuthenticated,
                currentPath,
                error,
                tenantsList,
                isAuthenticatedFromRedux,
                isHantzTenant
            });

            if (isAuthenticated) {
                dispatch(
                    updateOktaAuthState({
                        isAuthenticated: isAuthenticated,
                        authState: authState
                    })
                );
            } else {
                dispatch(
                    updateOktaAuthState({
                        isAuthenticated: false,
                        authState: null
                    })
                );
            }
        };
        checkAuth();
    }, [authState]);

    // Mirror a logout that happened in another tab so every open tab redirects
    // to the login page instead of showing a stale session. A session-expired
    // logout surfaces the "Session expired" modal; a manual logout redirects
    // silently so a user-initiated sign-out doesn't look like an expiry.
    useEffect(() => {
        const unsubscribe = onCrossTabLogout(
            () => triggerSessionExpiredLogout({ broadcast: false }),
            () => {
                sessionStorage.setItem('isLoggingOut', 'true');
                window.location.assign('/login');
            }
        );
        return unsubscribe;
    }, []);

    // Handle navigation based on authentication state
    useEffect(() => {
        if (!authState) return;
        // Don't navigate if logout is in progress (Okta signOut will handle redirect)
        const isLoggingOut = sessionStorage.getItem('isLoggingOut');
        console.log('AuthProvider: isLoggingOut flag:', isLoggingOut);
        if (isLoggingOut) {
            console.log('AuthProvider: Skipping navigation due to logout in progress');
            return;
        }

        const isAuthenticated = authState.isAuthenticated;
        const currentPath = location.pathname;
        const isAuthEntryRoute = AUTH_ENTRY_ROUTES.includes(currentPath);

        console.log('AuthProvider navigation check: 22', {
            authState,
            isAuthenticated,
            currentPath,
            error,
            tenantsList,
            isAuthenticatedFromRedux,
            isHantzTenant,
            sessionLoggingOut: sessionStorage.getItem('isLoggingOut')
        });

        if (authState.isAuthenticated) {
            // User is authenticated and has tenant info
            // if ( currentPath === ADVISOR_ROUTES.ROOT || currentPath === ADVISOR_ROUTES.LOGIN) {
            if (error?.message === 'Rejected') {
                navigate(ADVISOR_ROUTES.ACCESS_DENIED, { replace: true });
            } else if (tenantsList && tenantsList.length > 0 && isAuthEntryRoute) {
                // Handle the loader and navigation logic from App.tsx
                if (isHantzTenant) {
                    dispatch(updateGlobalLoaderState(false));
                    navigate(ADVISOR_ROUTES.DASHBOARD, { replace: true });
                } else {
                    navigate(ADVISOR_ROUTES.ADVISOR_CONSOLE, { replace: true });
                }
            }
            // }
        } else {
            // User is not authenticated
            if (error?.message === 'Rejected') {
                if (currentPath !== ADVISOR_ROUTES.ACCESS_DENIED) {
                    navigate(ADVISOR_ROUTES.ACCESS_DENIED, { replace: true });
                }
            } else {
                // if (currentPath !== ADVISOR_ROUTES.LOGIN && currentPath !== ADVISOR_ROUTES.ROOT) {
                if (currentPath !== ADVISOR_ROUTES.LOGIN) {
                    navigate(ADVISOR_ROUTES.LOGIN, { replace: true });
                }
                // }
            }
        }
    }, [authState, error, isHantzTenant, location.pathname, tenantsList]);

    // Show loading while initializing
    // if (!isInitialized) {
    //     return null; // or return <div>Loading...</div> if you want a loading indicator
    // }

    return <>{children}</>;
};
